// Curated no-code catalog for BusinessRule expressions. Every predicate shape
// that appears in the BusinessRule table (inventoried across all 50 rulesets
// on beta, 2026-08-11) is described here as a field with a label, a value
// editor kind, and the RulesEngine expression it expands to — the builder UI
// composes these with AND/OR groups and never exposes the underlying C#.
// Two domains exist: asset-tag rules (model = asset snapshot from the tag
// pipeline) and API request validation rules (model = request DTO); request
// fields are labeled "Request:" in the picker. Extend FIELDS as new predicate
// shapes show up in rules.

const MATERIAL = 'lookups.MaterialTypes.Single(s => s.Id == model.material_type)';
const SCAQMD_COUNT = 'lookups.Scaqmd_ZipCodes.Count(s => s.ZipCode.ToString() == model.zipcode)';

export const FIELDS = [
    // --- asset-tag rules ---
    { key: 'state', label: 'Location state', kind: 'text', lhs: 'model.state', hint: 'Two-letter code, e.g. CA' },
    { key: 'zipcode_scaqmd', label: 'SCAQMD zip code area', kind: 'scaqmdFlag' },
    { key: 'asset_type', label: 'Asset type ID(s)', kind: 'numberList', lhs: 'model.asset_type', hint: 'e.g. 22, 31, 63' },
    { key: 'asset_subtype_id', label: 'Asset subtype ID', kind: 'number', lhs: 'model.asset_subtype_id' },
    { key: 'charge_capacity_lbs', label: 'Charge capacity (lbs)', kind: 'number', lhs: 'model.charge_capacity_lbs' },
    { key: 'material_ar4_gwp', label: 'Material GWP (AR4 100-yr)', kind: 'number', lhs: `${MATERIAL}.Ar4100Gwp` },
    { key: 'material_ar5_gwp', label: 'Material GWP (AR5 100-yr)', kind: 'number', lhs: `${MATERIAL}.Ar5100Gwp` },
    { key: 'material_ar6_20_gwp', label: 'Material GWP (AR6 20-yr)', kind: 'number', lhs: `${MATERIAL}.Ar620Gwp` },
    { key: 'refrigerant_type', label: 'Refrigerant type ID(s)', kind: 'numberList', lhs: `${MATERIAL}.RefrigerantTypeId`, hint: 'e.g. 1, 2, 3' },
    { key: 'contains_aim', label: 'Contains AIM regulated substance', kind: 'flag01', lhs: `${MATERIAL}.ContainsAimRegulatedSubstance` },
    { key: 'has_ald', label: 'Leak monitoring device (ALD)', kind: 'aldFlag' },
    { key: 'leak_rate_epa_annualizing', label: 'Leak rate — EPA annualizing', kind: 'number', lhs: 'model.leak_rate_epa_annualizing', hint: 'Fraction, e.g. 0.20' },
    { key: 'leak_rate_epa_rolling_average', label: 'Leak rate — EPA rolling average', kind: 'number', lhs: 'model.leak_rate_epa_rolling_average', hint: 'Fraction, e.g. 0.20' },
    { key: 'leak_rate_aim_rolling_average', label: 'Leak rate — AIM rolling average', kind: 'number', lhs: 'model.leak_rate_epa_rolling_averange_aim', hint: 'Fraction, e.g. 0.20' },
    { key: 'leak_rate_12_month', label: 'Leak rate — 12-month rolling average', kind: 'number', lhs: 'model.leak_rate_12_month_rolling_average', hint: 'Fraction, e.g. 0.16' },
    // AB#16909: trailing-365-day MAX of each method (the plain fields above are the current
    // value at the latest DWH date_key). 0 = no leak rate on record; drives Healthy/Low/Near.
    { key: 'leak_rate_epa_annualizing_max365', label: 'Leak rate 365-day max — EPA annualizing', kind: 'number', lhs: 'model.leak_rate_epa_annualizing_max365', hint: 'Fraction, e.g. 0.10' },
    { key: 'leak_rate_epa_rolling_average_max365', label: 'Leak rate 365-day max — EPA rolling average', kind: 'number', lhs: 'model.leak_rate_epa_rolling_average_max365', hint: 'Fraction, e.g. 0.10' },
    { key: 'leak_rate_aim_rolling_average_max365', label: 'Leak rate 365-day max — AIM rolling average', kind: 'number', lhs: 'model.leak_rate_epa_rolling_averange_aim_max365', hint: 'Fraction, e.g. 0.10' },
    { key: 'leak_rate_12_month_max365', label: 'Leak rate 365-day max — 12-month rolling average', kind: 'number', lhs: 'model.leak_rate_12_month_rolling_average_max365', hint: 'Fraction, e.g. 0.08' },
    { key: 'days_since_leak_inspection', label: 'Days since leak inspection', kind: 'number', lhs: 'model.days_since_leak_inspection' },
    { key: 'days_since_ald_calibration', label: 'Days since ALD calibration', kind: 'number', lhs: 'model.days_since_ald_calibration' },
    { key: 'days_since_installation', label: 'Days since installation', kind: 'number', lhs: 'model.days_since_installation' },
    { key: 'has_tag', label: 'Has tag', kind: 'tags' },
    // --- API request validation rules ---
    { key: 'req_location_name', label: 'Request: Location name', kind: 'requiredText', lhs: 'model.LocationName' },
    { key: 'req_location_type', label: 'Request: Location type', kind: 'nullableNumber', lhs: 'model.LocationType' },
    { key: 'req_physical_street', label: 'Request: Physical street', kind: 'requiredText', lhs: 'model.PhysicalStreet' },
    { key: 'req_physical_city', label: 'Request: Physical city', kind: 'requiredText', lhs: 'model.PhysicalCity' },
    { key: 'req_physical_state', label: 'Request: Physical state', kind: 'requiredText', lhs: 'model.PhysicalState' },
    { key: 'req_physical_zip', label: 'Request: Physical zip', kind: 'requiredText', lhs: 'model.PhysicalZip' },
    { key: 'req_physical_country', label: 'Request: Physical country', kind: 'requiredText', lhs: 'model.PhysicalCountry' },
    { key: 'req_request_details', label: 'Request: Work order description', kind: 'requiredText', lhs: 'model.RequestDetails' },
    { key: 'req_location_id', label: 'Request: Location ID', kind: 'number', lhs: 'model.LocationId' },
    { key: 'req_service_company_id', label: 'Request: Service company ID', kind: 'number', lhs: 'model.ServiceCompanyID' },
    { key: 'req_work_order_id', label: 'Request: Work order ID', kind: 'number', lhs: 'model.ID' },
    { key: 'req_status_reason_id', label: 'Request: Status reason ID', kind: 'number', lhs: 'model.StatusReasonID' },
    { key: 'req_type_id', label: 'Request: Work order type ID', kind: 'number', lhs: 'model.TypeID' },
    { key: 'req_type_valid', label: 'Request: Work order type', kind: 'woTypeValid' },
    { key: 'req_priority_id', label: 'Request: Priority ID', kind: 'number', lhs: 'model.PriorityID' },
    { key: 'req_priority_valid', label: 'Request: Work order priority', kind: 'woPriorityValid' },
    { key: 'req_model_value', label: 'Request: ID value (bare model)', kind: 'number', lhs: 'model', hint: 'Rules whose whole model is one ID, e.g. GetWorkOrderById' },
    { key: 'req_page', label: 'Request: Page', kind: 'number', lhs: 'model.Page' },
    { key: 'req_row_count', label: 'Request: Row count', kind: 'number', lhs: 'model.RowCount' },
    { key: 'req_limit', label: 'Request: Limit', kind: 'number', lhs: 'model.limit' },
    { key: 'req_offset', label: 'Request: Offset', kind: 'number', lhs: 'model.offset' },
];

const NUMBER_OPERATORS = [
    { value: '==', name: '=' },
    { value: '!=', name: '≠' },
    { value: '>', name: '>' },
    { value: '>=', name: '≥' },
    { value: '<', name: '<' },
    { value: '<=', name: '≤' },
];

export const OPERATORS_BY_KIND = {
    text: [
        { value: '==', name: 'is' },
        { value: '!=', name: 'is not' },
    ],
    requiredText: [
        { value: 'provided', name: 'is provided' },
        { value: '==', name: 'is' },
        { value: '!=', name: 'is not' },
    ],
    number: NUMBER_OPERATORS,
    nullableNumber: [
        { value: 'provided', name: 'is provided (> 0)' },
        ...NUMBER_OPERATORS,
    ],
    numberList: [
        { value: 'in', name: 'is any of' },
    ],
    flag01: [
        { value: 'yes', name: 'yes' },
        { value: 'no', name: 'no' },
    ],
    aldFlag: [
        { value: 'yes', name: 'present' },
        { value: 'no', name: 'not present' },
    ],
    scaqmdFlag: [
        { value: 'yes', name: 'inside' },
        { value: 'no', name: 'outside' },
    ],
    woTypeValid: [
        { value: 'valid', name: 'is a valid type' },
    ],
    woPriorityValid: [
        { value: 'valid', name: 'is a valid priority' },
    ],
    tags: [
        { value: 'any', name: 'any of' },
        // AB#16909: negated form — "carries none of these tags (within the recency window)".
        // The status tiers use it to subordinate Healthy/Low/Near to the Hit trigger tags.
        { value: 'none', name: 'none of' },
    ],
};

// Tag recency choices for the has-tag condition (t.days_since_active).
export const TAG_RECENCY_OPTIONS = [
    { value: '', name: 'any time' },
    { value: 'today', name: 'active today' },
    { value: 'within', name: 'within N days' },
    { value: 'atleast', name: 'not active for N days' },
];

// Kinds whose operator alone carries the whole condition (no value editor).
export const VALUELESS_KINDS = ['flag01', 'aldFlag', 'scaqmdFlag', 'woTypeValid', 'woPriorityValid'];

export const conditionNeedsValue = (field, operator) => {
    if (!field) return false;
    if (VALUELESS_KINDS.includes(field.kind)) return false;
    if (operator === 'provided') return false;
    return true;
};

export const getField = (key) => FIELDS.find(f => f.key === key);

export const emptyGroup = () => ({ combinator: 'and', items: [] });

export const emptyCondition = () => ({ type: 'condition', field: '', operator: '', value: '' });

export const defaultValueFor = (field) => (field.kind === 'tags' ? { names: [], recency: '', days: '' } : '');

// Validates numeric input but preserves the text as typed so values like
// 0.20 round-trip exactly against existing expressions.
const numericText = (raw) => {
    const text = String(raw ?? '').trim();
    return text !== '' && Number.isFinite(Number(text)) ? text : null;
};

const generateCondition = (condition, errors) => {
    const field = getField(condition.field);
    if (!field) {
        errors.push('Every condition needs a field.');
        return null;
    }

    switch (field.kind) {
        case 'text': {
            const value = String(condition.value || '').trim();
            if (!value) {
                errors.push(`${field.label}: enter a value.`);
                return null;
            }
            return `${field.lhs} ${condition.operator} "${value}"`;
        }
        case 'requiredText': {
            if (condition.operator === 'provided') {
                return `${field.lhs} != null && ${field.lhs} != ""`;
            }
            const value = String(condition.value || '').trim();
            if (!value) {
                errors.push(`${field.label}: enter a value.`);
                return null;
            }
            return `${field.lhs} ${condition.operator} "${value}"`;
        }
        case 'number':
        case 'nullableNumber': {
            if (field.kind === 'nullableNumber' && condition.operator === 'provided') {
                return `${field.lhs} != null && ${field.lhs} > 0`;
            }
            const value = numericText(condition.value);
            if (value === null) {
                errors.push(`${field.label}: enter a number.`);
                return null;
            }
            return `${field.lhs} ${condition.operator} ${value}`;
        }
        case 'numberList': {
            const values = String(condition.value || '')
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
                .map(numericText);
            if (values.length === 0 || values.some(v => v === null)) {
                errors.push(`${field.label}: enter one or more numbers separated by commas.`);
                return null;
            }
            const parts = values.map(v => `${field.lhs} == ${v}`);
            return parts.length === 1 ? parts[0] : `(${parts.join(' || ')})`;
        }
        case 'flag01':
            return `${field.lhs} == ${condition.operator === 'yes' ? 1 : 0}`;
        case 'aldFlag':
            return condition.operator === 'yes'
                ? '!string.IsNullOrEmpty(model.leak_monitoring_device)'
                : 'string.IsNullOrEmpty(model.leak_monitoring_device)';
        case 'scaqmdFlag':
            return `${SCAQMD_COUNT} ${condition.operator === 'yes' ? '>' : '=='} 0`;
        case 'woTypeValid':
            return 'model.TypeID > 0 && lookups.WorkOrderTypes.Contains(model.TypeID)';
        case 'woPriorityValid':
            return 'model.PriorityID > 0 && lookups.WorkOrderPriorities.Contains(model.PriorityID.Value)';
        case 'tags': {
            const names = (condition.value && condition.value.names) || [];
            if (names.length === 0) {
                errors.push('Has tag: pick at least one tag.');
                return null;
            }
            // 'none' negates the whole Any — "carries none of these tags (in the window)".
            const not = condition.operator === 'none' ? '!' : '';
            const inner = names.map(n => `t.name == "${n}"`).join(' || ');
            const wrapped = names.length > 1 ? `(${inner})` : inner;
            // back-compat: earlier builder states stored only { names, days }
            const recency = condition.value.recency ?? (numericText(condition.value.days) !== null ? 'within' : '');
            if (!recency) {
                return `${not}model.tags.Any(t => ${wrapped})`;
            }
            if (recency === 'today') {
                return `${not}model.tags.Any(t => (${inner}) && t.days_since_active == 0)`;
            }
            const days = numericText(condition.value.days);
            if (days === null) {
                errors.push('Has tag: enter the number of days.');
                return null;
            }
            return `${not}model.tags.Any(t => (${inner}) && t.days_since_active ${recency === 'atleast' ? '>=' : '<='} ${days})`;
        }
        default:
            errors.push(`${field.label}: unsupported field kind.`);
            return null;
    }
};

const isFullyWrapped = (part) => {
    if (!part.startsWith('(') || !part.endsWith(')')) return false;
    let depth = 0;
    for (let i = 0; i < part.length; i++) {
        if (part[i] === '(') depth++;
        else if (part[i] === ')') depth--;
        if (depth === 0 && i < part.length - 1) return false;
    }
    return depth === 0;
};

// Compound conditions (containing a top-level &&) need parens inside an OR
// group to preserve precedence; && groups don't, since && binds tighter.
// tags.Any conditions (plain or negated) keep their && inside the lambda parens.
const needsParensInOr = (part) =>
    part.includes(' && ') && !part.startsWith('model.tags.Any(') && !part.startsWith('!model.tags.Any(') && !isFullyWrapped(part);

const generateGroup = (group, errors, isRoot) => {
    const parts = (group.items || [])
        .map(item => (item.type === 'group'
            ? generateGroup(item, errors, false)
            : generateCondition(item, errors)))
        .filter(Boolean);

    if ((group.items || []).length === 0) {
        errors.push(isRoot ? 'Add at least one condition.' : 'Every group needs at least one condition.');
        return null;
    }

    if (parts.length === 0 || parts.length < group.items.length) {
        // a condition inside already reported its own error
        return null;
    }

    const isOr = group.combinator === 'or';
    const finalParts = isOr && parts.length > 1
        ? parts.map(p => (needsParensInOr(p) ? `(${p})` : p))
        : parts;
    const joined = finalParts.join(isOr ? ' || ' : ' && ');
    return isRoot || parts.length === 1 ? joined : `(${joined})`;
};

// Returns { expression, errors }. expression is null when errors is non-empty.
export const generateExpression = (root) => {
    const errors = [];
    const expression = generateGroup(root, errors, true);
    return { expression: errors.length === 0 ? expression : null, errors };
};

// Whitespace-insensitive comparison for detecting whether an expression was
// edited outside the builder after its BuilderState was saved.
export const expressionsMatch = (a, b) =>
    String(a || '').replace(/\s+/g, '') === String(b || '').replace(/\s+/g, '');

// ---------------------------------------------------------------------------
// Reverse parser: maps an existing expression back into builder state so
// hand-written rules load into the builder. Because every predicate shape in
// the table comes from this catalog's templates, parsing is template matching
// — not general C# parsing. Returns null when any part doesn't match, and the
// builder falls back to starting empty.

const splitTopLevel = (text, separator) => {
    const parts = [];
    let depth = 0;
    let inString = false;
    let start = 0;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch === '"') inString = !inString;
        else if (!inString && ch === '(') depth++;
        else if (!inString && ch === ')') depth--;
        else if (!inString && depth === 0 && text.startsWith(separator, i)) {
            parts.push(text.slice(start, i).trim());
            start = i + separator.length;
            i += separator.length - 1;
        }
    }
    parts.push(text.slice(start).trim());
    return parts;
};

const startsWithLhs = (token, lhs) => token.toLowerCase().startsWith(`${lhs.toLowerCase()} `);

const parseTagNames = (text) => {
    const names = [];
    for (const part of splitTopLevel(text, ' || ')) {
        const m = part.match(/^t\.name == "(.*)"$/);
        if (!m) return null;
        names.push(m[1]);
    }
    return names;
};

const stripOuterParens = (text) =>
    (isFullyWrapped(text) ? text.slice(1, -1).trim() : text);

const parseLeaf = (token) => {
    if (token === '!string.IsNullOrEmpty(model.leak_monitoring_device)')
        return { type: 'condition', field: 'has_ald', operator: 'yes', value: '' };
    if (token === 'string.IsNullOrEmpty(model.leak_monitoring_device)')
        return { type: 'condition', field: 'has_ald', operator: 'no', value: '' };

    const scaqmd = token.match(/^lookups\.Scaqmd_ZipCodes\.Count\(s => s\.ZipCode\.ToString\(\) == model\.zipcode\) (>|==) 0$/i);
    if (scaqmd)
        return { type: 'condition', field: 'zipcode_scaqmd', operator: scaqmd[1] === '>' ? 'yes' : 'no', value: '' };

    const tags = token.match(/^(!?)model\.tags\.Any\(t => (.+)\)$/);
    if (tags) {
        const operator = tags[1] === '!' ? 'none' : 'any';
        const inner = tags[2].trim();
        const recencySplit = splitTopLevel(inner, ' && ');
        if (recencySplit.length === 2) {
            const names = parseTagNames(stripOuterParens(recencySplit[0]));
            const rec = recencySplit[1].match(/^t\.days_since_active (<=|==|>=) (\d+)$/);
            if (!names || !rec) return null;
            if (rec[1] === '==' && rec[2] !== '0') return null;
            const recency = rec[1] === '==' ? 'today' : (rec[1] === '<=' ? 'within' : 'atleast');
            return { type: 'condition', field: 'has_tag', operator, value: { names, recency, days: recency === 'today' ? '' : rec[2] } };
        }
        const names = parseTagNames(stripOuterParens(inner));
        if (!names) return null;
        return { type: 'condition', field: 'has_tag', operator, value: { names, recency: '', days: '' } };
    }

    // number-list expansion: (lhs == a || lhs == b || ...)
    if (isFullyWrapped(token)) {
        const inner = stripOuterParens(token);
        const parts = splitTopLevel(inner, ' || ');
        if (parts.length > 1) {
            for (const field of FIELDS.filter(f => f.kind === 'numberList')) {
                const values = [];
                const allMatch = parts.every(part => {
                    if (!startsWithLhs(part, field.lhs)) return false;
                    const m = part.slice(field.lhs.length).trim().match(/^== (-?[\d.]+)$/);
                    if (!m) return false;
                    values.push(m[1]);
                    return true;
                });
                if (allMatch)
                    return { type: 'condition', field: field.key, operator: 'in', value: values.join(', ') };
            }
        }
        return null;
    }

    // lhs-based fields: try the longest-matching lhs so e.g. the material
    // lookup chains win over plain model.* prefixes
    const candidates = FIELDS
        .filter(f => f.lhs && startsWithLhs(token, f.lhs))
        .sort((a, b) => b.lhs.length - a.lhs.length);
    for (const field of candidates) {
        const rest = token.slice(field.lhs.length).trim();
        if (field.kind === 'text' || field.kind === 'requiredText') {
            const m = rest.match(/^(==|!=) "(.*)"$/);
            if (m) return { type: 'condition', field: field.key, operator: m[1], value: m[2] };
        }
        if (field.kind === 'number' || field.kind === 'nullableNumber') {
            const m = rest.match(/^(==|!=|>=|<=|>|<) (-?[\d.]+)$/);
            if (m) return { type: 'condition', field: field.key, operator: m[1], value: m[2] };
        }
        if (field.kind === 'numberList') {
            const m = rest.match(/^== (-?[\d.]+)$/);
            if (m) return { type: 'condition', field: field.key, operator: 'in', value: m[1] };
        }
        if (field.kind === 'flag01') {
            const m = rest.match(/^== (0|1)$/);
            if (m) return { type: 'condition', field: field.key, operator: m[1] === '1' ? 'yes' : 'no', value: '' };
        }
    }

    return null;
};

// Merges adjacent &&-chain tokens that together form one compound-template
// condition (required text, provided number, valid type/priority).
const mergeCompoundPairs = (tokens) => {
    const merged = [];
    for (let i = 0; i < tokens.length; i++) {
        const current = tokens[i];
        const next = tokens[i + 1];
        if (next !== undefined) {
            if (current === 'model.TypeID > 0' && next === 'lookups.WorkOrderTypes.Contains(model.TypeID)') {
                merged.push({ type: 'condition', field: 'req_type_valid', operator: 'valid', value: '' });
                i++;
                continue;
            }
            if (current === 'model.PriorityID > 0' && next === 'lookups.WorkOrderPriorities.Contains(model.PriorityID.Value)') {
                merged.push({ type: 'condition', field: 'req_priority_valid', operator: 'valid', value: '' });
                i++;
                continue;
            }
            const nullCheck = current.match(/^(.+) != null$/);
            if (nullCheck) {
                const lhs = nullCheck[1];
                if (next === `${lhs} != ""`) {
                    const field = FIELDS.find(f => f.kind === 'requiredText' && f.lhs.toLowerCase() === lhs.toLowerCase());
                    if (field) {
                        merged.push({ type: 'condition', field: field.key, operator: 'provided', value: '' });
                        i++;
                        continue;
                    }
                }
                if (next === `${lhs} > 0`) {
                    const field = FIELDS.find(f => f.kind === 'nullableNumber' && f.lhs.toLowerCase() === lhs.toLowerCase());
                    if (field) {
                        merged.push({ type: 'condition', field: field.key, operator: 'provided', value: '' });
                        i++;
                        continue;
                    }
                }
            }
        }
        merged.push(current);
    }
    return merged;
};

const parseItem = (text) => {
    const leaf = parseLeaf(text);
    if (leaf) return leaf;
    if (isFullyWrapped(text)) {
        const group = parseGroupText(stripOuterParens(text));
        // collapse single-condition groups into the condition itself
        if (group.items.length === 1 && group.items[0].type === 'condition') return group.items[0];
        return { type: 'group', ...group };
    }
    if (splitTopLevel(text, ' && ').length > 1 || splitTopLevel(text, ' || ').length > 1) {
        const group = parseGroupText(text);
        if (group.items.length === 1 && group.items[0].type === 'condition') return group.items[0];
        return { type: 'group', ...group };
    }
    throw new Error(`Unrecognized expression part: ${text}`);
};

const parseGroupText = (text) => {
    const orParts = splitTopLevel(text, ' || ');
    if (orParts.length > 1) {
        return { combinator: 'or', items: orParts.map(parseItem) };
    }
    const tokens = splitTopLevel(text, ' && ');
    const items = mergeCompoundPairs(tokens)
        .map(token => (typeof token === 'string' ? parseItem(token) : token));
    return { combinator: 'and', items };
};

// Returns builder state for the expression, or null if any part of it
// doesn't map onto the catalog.
export const parseExpression = (expression) => {
    const text = String(expression || '').replace(/\s+/g, ' ').trim();
    if (!text) return null;
    try {
        const group = parseGroupText(text);
        return { combinator: group.combinator, items: group.items };
    } catch (error) {
        return null;
    }
};
